import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMeetings,
  getUpcomingMeetings,
  getMeeting,
  createMeeting,
  updateMeeting,
  startMeeting,
  completeMeeting,
  getActionItems,
  getOpenItemsForBookkeeper,
  createActionItem,
  updateActionItem,
  completeActionItem,
  carryForwardActionItem,
  batchUpdateActionItems,
  getAgendaPreview,
  recordAgendaOutcome,
  getNextMeetingProposal,
  setActionItemMeetingFlag,
  setReviewMeetingFlag,
} from '../api/meetings';

// The agenda (docs/specs/meetings-agenda.md)
export function useAgendaPreview(bookkeeperId: string | undefined) {
  return useQuery({
    queryKey: ['meetings', 'agenda-preview', bookkeeperId],
    queryFn: () => getAgendaPreview(bookkeeperId!),
    enabled: !!bookkeeperId,
  });
}

export function useRecordAgendaOutcome(meetingId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { lineId: string } & Parameters<typeof recordAgendaOutcome>[2]) => {
      const { lineId, ...outcome } = v;
      return recordAgendaOutcome(meetingId!, lineId, outcome);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting', meetingId] });
    },
  });
}

export function useNextMeetingProposal(meetingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['meeting', meetingId, 'next-proposal'],
    queryFn: () => getNextMeetingProposal(meetingId!),
    enabled: !!meetingId && enabled,
  });
}

export function useSetActionItemMeetingFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; flagged: boolean }) => setActionItemMeetingFlag(v.id, v.flagged),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      queryClient.invalidateQueries({ queryKey: ['meetings', 'agenda-preview'] });
    },
  });
}

export function useSetReviewMeetingFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; flagged: boolean }) => setReviewMeetingFlag(v.id, v.flagged),
    onSuccess: (_, v) => {
      queryClient.invalidateQueries({ queryKey: ['client-review', v.id] });
      queryClient.invalidateQueries({ queryKey: ['meetings', 'agenda-preview'] });
    },
  });
}

// Meetings hooks
export function useMeetings(params?: {
  supervisorId?: string;
  bookkeeperId?: string;
  status?: string;
}) {
  return useQuery({
    queryKey: ['meetings', params],
    queryFn: () => getMeetings(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useUpcomingMeetings(daysAhead: number = 7) {
  return useQuery({
    queryKey: ['meetings', 'upcoming', daysAhead],
    queryFn: () => getUpcomingMeetings(daysAhead),
    staleTime: 1000 * 60 * 2,
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: ['meeting', id],
    queryFn: () => getMeeting(id!),
    enabled: !!id,
  });
}

export function useCreateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMeeting,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateMeeting>[1] }) =>
      updateMeeting(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['meeting', id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useStartMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: startMeeting,
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: ['meeting', meeting.id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useCompleteMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: completeMeeting,
    onSuccess: (meeting) => {
      queryClient.invalidateQueries({ queryKey: ['meeting', meeting.id] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

// Action Items hooks
export function useActionItems(params?: {
  bookkeeperId?: string;
  status?: string;
  supervisorId?: string;
}) {
  return useQuery({
    queryKey: ['action-items', params],
    queryFn: () => getActionItems(params),
    staleTime: 1000 * 60 * 2,
  });
}

export function useOpenItemsForBookkeeper(bookkeeperId: string | undefined) {
  return useQuery({
    queryKey: ['action-items', 'open', bookkeeperId],
    queryFn: () => getOpenItemsForBookkeeper(bookkeeperId!),
    enabled: !!bookkeeperId,
  });
}

export function useCreateActionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createActionItem,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
      // Also invalidate the review if action item was created from a review
      if (variables.sourceReviewId) {
        queryClient.invalidateQueries({ queryKey: ['client-review', variables.sourceReviewId] });
      }
    },
  });
}

export function useUpdateActionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateActionItem>[1] }) =>
      updateActionItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
    },
  });
}

export function useCompleteActionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: Parameters<typeof completeActionItem>[1] }) =>
      completeActionItem(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useCarryForwardActionItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, meetingId }: { id: string; meetingId?: string }) =>
      carryForwardActionItem(id, meetingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}

export function useBatchUpdateActionItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ids, status, resolutionNotes }: { ids: string[]; status: string; resolutionNotes?: string }) =>
      batchUpdateActionItems(ids, status, resolutionNotes),
    onSuccess: () => {
      // The backend also fans out a single actionItem.batchUpdated SignalR event that invalidates
      // ['action-items'] app-wide; this covers the initiating tab immediately.
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });
}
